/* eslint-disable react/no-children-prop */
import React from "react";
import { useTranslation } from "react-i18next";
// UI
import { getRouteApi } from "@tanstack/react-router";
import { Box, Flex, Stack, Image } from "@mantine/core";
import { useAppForm } from "@components/form/app-form";
import { notifications } from "@mantine/notifications";
import { TwoColLayout } from "@components/two-col-layout";
import {
  SpringOnboardingText,
  SpringOnboardingTitle,
} from "@components/Typography";
//
import { useSpringOnboardingStore } from "src/services/spring-health-onboarding-store";
import { useEnrollMutation } from "src/backendApi/queries/spring-health-queries";
//
import { z } from "zod";
//
import ImageForThisPage from "src/assets/mightier-adventure.png";

const formSchema = z.object({
  playerUsername: z.string().trim().min(2),
});

const Route = getRouteApi("/spring-health/register");

export function SpringRegisterEnrollPage(): React.ReactElement {
  const { t } = useTranslation();

  const userCreateFormData = useSpringOnboardingStore(
    (store) => store.payload?.userCreate,
  );

  const nav = Route.useNavigate();

  const { does_account_exist: doesAccountExist } = Route.useLoaderData();

  const searchParams = Route.useSearch();

  const { mutateAsync: enroll } = useEnrollMutation({
    onSuccess: (data) => {
      const accountActivationUrl = data.account_activation_url;
      if (accountActivationUrl) {
        window.location.href = accountActivationUrl;
      } else {
        nav({
          to: "/spring-health/thank-you",
          search: { newAccountCreated: false },
        });
      }
    },
    onError: () => {
      notifications.show({
        title: t("springHealth.verify.submitNotifications.errorTitle"),
        message: t("springHealth.verify.submitNotifications.errorBody"),
        color: "red",
      });
    },
  });

  const form = useAppForm({
    defaultValues: {
      playerUsername: "",
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      // Re-parse to get the transformed (trimmed) output
      const validatedData = formSchema.parse(value);

      // if account already exists - it's simple
      if (doesAccountExist) {
        await enroll({
          accountExists: true,
          payload: {
            ...searchParams,
            player_username: validatedData.playerUsername,
          },
        });
        return;
      }

      // if account doesn't already exist, then we need to
      if (!userCreateFormData) {
        throw new Error(
          "Non-existent user and no data from onboarding form saved! Cannot continue",
        );
      }

      await enroll({
        accountExists: false,
        payload: {
          ...searchParams,
          player_username: validatedData.playerUsername,
          user: {
            first_name: userCreateFormData.firstName,
            last_name: userCreateFormData.lastName,
            phonenumber: userCreateFormData.phoneNum,
            //
            shipping_information: {
              address_1: userCreateFormData.addr1,
              address_2: userCreateFormData.addr2,
              city: userCreateFormData.city,
              state: userCreateFormData.provinceCode,
              country: userCreateFormData.country,
              zipcode: userCreateFormData.zip,
            },
            notification_preferences: {
              is_email_ok: userCreateFormData.emailOptIn,
              is_sms_ok: false,
            },
          },
        },
      });
    },
  });

  return (
    <Box mt={{ xs: 0, md: "xl" }}>
      <TwoColLayout
        leftChildren={
          <Stack px="sm" mt={{ xs: 0, md: "xl" }}>
            <SpringOnboardingTitle order={3}>
              {t("springHealth.playerName.title")}
            </SpringOnboardingTitle>
            <SpringOnboardingText>
              {t("springHealth.playerName.description")}
            </SpringOnboardingText>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit();
              }}
            >
              <form.AppField
                name="playerUsername"
                children={(field) => {
                  return (
                    <field.TextField
                      placeholder={t(
                        "springHealth.playerName.inputPlaceholder",
                      )}
                      errors={field.state.meta.errors}
                      data-testid="player-username"
                    />
                  );
                }}
              />

              <Flex mt="md">
                <form.Subscribe
                  selector={(state) => [
                    state.canSubmit,
                    state.isSubmitting,
                    state.isDefaultValue,
                  ]}
                  children={([canSubmit, isSubmitting, isDefaultValue]) => (
                    <form.SubmitButton
                      loading={isSubmitting}
                      disabled={!canSubmit || isDefaultValue}
                      data-testid="submit-btn"
                    >
                      {t("springHealth.playerName.submitButton")}
                    </form.SubmitButton>
                  )}
                />
              </Flex>
            </form>
          </Stack>
        }
        rightChildren={<Image src={ImageForThisPage} />}
      />
    </Box>
  );
}
